import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api, { filesAPI } from '../utils/api';
import {
  FileText, Upload, Trash2, Download, HardDrive, Cpu, BookOpen, File,
  AlertCircle, CheckCircle, X, Users, Search, Pencil
} from 'lucide-react';

const CATEGORY_META = {
  firmware: { icon: Cpu, color: '#f59e0b', label: 'Firmware', labelHe: 'קושחה' },
  manual: { icon: BookOpen, color: '#8b5cf6', label: 'Manual', labelHe: 'מדריך' },
  document: { icon: FileText, color: '#3b82f6', label: 'Document', labelHe: 'מסמך' },
  general: { icon: File, color: '#6b7280', label: 'General', labelHe: 'כללי' },
};

function formatSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export default function FilesPage() {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const isHe = i18n.language === 'he';
  const isAdmin = user?.role === 'admin';
  const fileInputRef = useRef(null);

  const [files, setFiles] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState(null);

  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCategory, setUploadCategory] = useState('general');
  const [uploadDesc, setUploadDesc] = useState('');
  const [uploadVisibility, setUploadVisibility] = useState('private');
  const [uploadSelectedUsers, setUploadSelectedUsers] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Edit state
  const [editingFile, setEditingFile] = useState(null);

  useEffect(() => { loadFiles(); if (isAdmin) loadUsers(); }, []);

  const loadFiles = async () => {
    try { const { data } = await filesAPI.list(); setFiles(data); }
    catch { setMessage({ type: 'error', text: 'Failed to load files' }); }
    finally { setLoading(false); }
  };

  const loadUsers = async () => {
    try { const { data } = await api.get('/profiles/users/list'); setUsersList(data); } catch {}
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true); setMessage(null);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('category', uploadCategory);
      formData.append('description', uploadDesc);
      formData.append('visibility', isAdmin ? uploadVisibility : 'private');
      if (isAdmin && uploadVisibility === 'assigned')
        formData.append('assignedUserIds', JSON.stringify(uploadSelectedUsers));
      await filesAPI.upload(formData);
      setMessage({ type: 'success', text: isHe ? 'הקובץ הועלה בהצלחה' : 'File uploaded' });
      setShowUpload(false); setUploadFile(null); setUploadDesc('');
      setUploadVisibility('private'); setUploadSelectedUsers([]); loadFiles();
    } catch (err) { setMessage({ type: 'error', text: err.response?.data?.error || 'Upload failed' }); }
    finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(isHe ? 'למחוק?' : 'Delete?')) return;
    try { await filesAPI.delete(id); setFiles(prev => prev.filter(f => f.id !== id)); }
    catch { setMessage({ type: 'error', text: 'Failed to delete' }); }
  };

  const handleDownload = (id) => {
    const token = localStorage.getItem('sophistiscan-token');
    window.open(`${filesAPI.downloadUrl(id)}?token=${token}`, '_blank');
  };

  const handleEditSave = async (updatedData) => {
    try {
      const { data } = await filesAPI.update(editingFile.id, updatedData);
      setFiles(prev => prev.map(f => f.id === editingFile.id ? { ...f, ...data } : f));
      setEditingFile(null);
      setMessage({ type: 'success', text: isHe ? 'הקובץ עודכן בהצלחה' : 'File updated' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      throw err;
    }
  };

  const filtered = files.filter(f => {
    if (filterCategory !== 'all' && f.category !== filterCategory) return false;
    if (searchTerm && !f.originalName.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !f.description?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-brand-500/30 border-t-brand-500 rounded-full animate-spin" /></div>;

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3" style={{color:'var(--text-primary)'}}>
            <HardDrive className="w-7 h-7 text-brand-500" />
            {isHe ? 'קבצים ומסמכים' : 'Files & Documents'}
          </h1>
          <p className="text-sm mt-1" style={{color:'var(--text-muted)'}}>
            {isHe ? 'קבצי קושחה, מדריכים ומסמכים' : 'Firmware, manuals and documents'}
          </p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowUpload(!showUpload)} className="btn-primary flex items-center gap-2">
            <Upload className="w-4 h-4" /> {isHe ? 'העלאת קובץ' : 'Upload File'}
          </button>
        )}
      </div>

      {message && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-lg mb-4 text-sm ${message.type === 'success' ? 'bg-green-500/10 border border-green-500/20 text-green-500' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {message.text}
          <button onClick={() => setMessage(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Upload Form */}
      {showUpload && isAdmin && (
        <div className="card p-6 mb-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2" style={{color:'var(--text-primary)'}}>
            <Upload className="w-5 h-5 text-brand-500" /> {isHe ? 'העלאת קובץ חדש' : 'Upload New File'}
          </h3>
          <div className="space-y-4">
            <input ref={fileInputRef} type="file" className="hidden" onChange={e => setUploadFile(e.target.files[0])} />
            {uploadFile ? (
              <div className="flex items-center gap-3 p-3 rounded-lg" style={{background:'var(--bg-input)'}}>
                <File className="w-5 h-5 text-brand-500" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{color:'var(--text-primary)'}}>{uploadFile.name}</p>
                  <p className="text-xs" style={{color:'var(--text-muted)'}}>{formatSize(uploadFile.size)}</p>
                </div>
                <button onClick={() => { setUploadFile(null); fileInputRef.current.value = ''; }} className="btn-ghost text-red-400"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <div onClick={() => fileInputRef.current?.click()} className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-brand-500/50 transition-colors" style={{borderColor:'var(--border)'}}>
                <Upload className="w-8 h-8 mx-auto mb-2" style={{color:'var(--text-muted)'}} />
                <p className="text-sm" style={{color:'var(--text-muted)'}}>{isHe ? 'לחץ לבחירת קובץ' : 'Click to select file'}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">{isHe ? 'קטגוריה' : 'Category'}</label>
                <select className="select-field" value={uploadCategory} onChange={e => setUploadCategory(e.target.value)}>
                  {Object.entries(CATEGORY_META).map(([key, meta]) => (
                    <option key={key} value={key}>{isHe ? meta.labelHe : meta.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">{isHe ? 'תיאור' : 'Description'}</label>
                <input type="text" className="input-field" value={uploadDesc} onChange={e => setUploadDesc(e.target.value)}
                  placeholder={isHe ? 'למשל: גרסה 2.1' : 'e.g. Version 2.1'} />
              </div>
            </div>

            {/* Visibility */}
            <div>
              <label className="label">{isHe ? 'מי יראה?' : 'Visibility'}</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {[
                  { val: 'private', label: isHe ? 'רק אני' : 'Only me' },
                  { val: 'all', label: isHe ? 'כל המשתמשים' : 'All users' },
                  { val: 'assigned', label: isHe ? 'ספציפיים' : 'Specific users' },
                ].map(opt => (
                  <label key={opt.val} className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer border transition-all"
                    style={{ borderColor: uploadVisibility === opt.val ? 'var(--brand)' : 'var(--border)', background: uploadVisibility === opt.val ? 'var(--brand-light)' : 'transparent' }}>
                    <input type="radio" name="fVis" value={opt.val} checked={uploadVisibility === opt.val}
                      onChange={() => setUploadVisibility(opt.val)} className="accent-brand-500" />
                    <span className="text-sm font-medium" style={{color:'var(--text-primary)'}}>{opt.label}</span>
                  </label>
                ))}
              </div>
              {uploadVisibility === 'assigned' && usersList.length > 0 && (
                <div className="mt-3 max-h-40 overflow-y-auto rounded-lg border p-2 space-y-1" style={{borderColor:'var(--border)', background:'var(--bg-card)'}}>
                  {usersList.filter(u => u.id !== user.id).map(u => (
                    <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-brand-500/5 cursor-pointer">
                      <input type="checkbox" className="accent-brand-500" checked={uploadSelectedUsers.includes(u.id)}
                        onChange={e => { if (e.target.checked) setUploadSelectedUsers(p => [...p, u.id]); else setUploadSelectedUsers(p => p.filter(id => id !== u.id)); }} />
                      <span className="text-sm" style={{color:'var(--text-primary)'}}>{u.displayName}</span>
                      <span className="text-xs" style={{color:'var(--text-muted)'}}>({u.email})</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button onClick={handleUpload} disabled={!uploadFile || uploading} className="btn-primary flex items-center gap-2">
                {uploading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Upload className="w-4 h-4" />}
                {isHe ? 'העלה' : 'Upload'}
              </button>
              <button onClick={() => { setShowUpload(false); setUploadFile(null); }} className="btn-secondary">{isHe ? 'ביטול' : 'Cancel'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{color:'var(--text-muted)'}} />
          <input type="text" className="input-field pl-10 text-sm" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder={isHe ? 'חיפוש...' : 'Search...'} />
        </div>
        <div className="flex gap-1 flex-wrap">
          {[{ key: 'all', label: isHe ? 'הכל' : 'All' }, ...Object.entries(CATEGORY_META).map(([k, m]) => ({ key: k, label: isHe ? m.labelHe : m.label }))].map(f => (
            <button key={f.key} onClick={() => setFilterCategory(f.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all`}
              style={filterCategory === f.key ? {borderColor:'var(--brand)', color:'var(--brand)', background:'var(--brand-light)'} : {borderColor:'var(--border)', color:'var(--text-secondary)'}}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Files */}
      {filtered.length === 0 ? (
        <div className="card p-12 text-center">
          <HardDrive className="w-12 h-12 mx-auto mb-3" style={{color:'var(--text-muted)'}} />
          <p style={{color:'var(--text-muted)'}}>{isHe ? 'אין קבצים' : 'No files'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(file => {
            const cm = CATEGORY_META[file.category] || CATEGORY_META.general;
            const CI = cm.icon;
            return (
              <div key={file.id} className="card p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0" style={{background:`${cm.color}15`, border:`1px solid ${cm.color}30`}}>
                  <CI className="w-5 h-5" style={{color:cm.color}} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium truncate" style={{color:'var(--text-primary)'}}>{file.description || file.originalName}</p>
                    <span className="badge" style={{background:`${cm.color}15`, color:cm.color, border:`1px solid ${cm.color}30`}}>{isHe ? cm.labelHe : cm.label}</span>
                    {isAdmin && file.visibility === 'all' && <span className="badge-green">{isHe ? 'לכולם' : 'All'}</span>}
                    {isAdmin && file.visibility === 'assigned' && <span className="badge-amber">{file.assignedUsers?.length || 0} {isHe ? 'משתמשים' : 'users'}</span>}
                  </div>
                  <p className="text-sm mt-0.5 truncate font-mono" style={{color:'var(--text-muted)'}}>{file.originalName}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs flex-wrap" style={{color:'var(--text-muted)'}}>
                    <span>{formatSize(file.fileSize)}</span>
                    <span>•</span>
                    <span>{new Date(file.createdAt).toLocaleDateString()}</span>
                    {file.downloadCount > 0 && <><span>•</span><span>{file.downloadCount} {isHe ? 'הורדות' : 'downloads'}</span></>}
                  </div>
                  {isAdmin && file.assignedUsers?.length > 0 && (
                    <p className="text-xs mt-1" style={{color:'var(--text-muted)'}}>{isHe ? 'משויך:' : 'Shared:'} {file.assignedUsers.map(u => u.displayName).join(', ')}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {(isAdmin || file.isOwner) && (
                    <button onClick={() => setEditingFile(file)} className="btn-ghost text-blue-400" title={isHe ? 'ערוך' : 'Edit'}>
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => handleDownload(file.id)} className="btn-primary text-xs flex items-center gap-1.5 px-3 py-2">
                    <Download className="w-3.5 h-3.5" /> {isHe ? 'הורד' : 'Download'}
                  </button>
                  {(isAdmin || file.isOwner) && (
                    <button onClick={() => handleDelete(file.id)} className="btn-ghost text-red-400"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Modal */}
      {editingFile && (
        <EditFileModal
          file={editingFile}
          isHe={isHe}
          isAdmin={isAdmin}
          usersList={usersList}
          currentUserId={user?.id}
          onSave={handleEditSave}
          onClose={() => setEditingFile(null)}
        />
      )}
    </div>
  );
}

// ===== Edit File Modal =====
function EditFileModal({ file, isHe, isAdmin, usersList, currentUserId, onSave, onClose }) {
  const [originalName, setOriginalName] = useState(file.originalName || '');
  const [category, setCategory] = useState(file.category || 'general');
  const [description, setDescription] = useState(file.description || '');
  const [visibility, setVisibility] = useState(file.visibility || 'private');
  const [selectedUsers, setSelectedUsers] = useState(
    file.assignedUsers?.map(u => u.id) || []
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!originalName.trim()) {
      setError(isHe ? 'שם הקובץ לא יכול להיות ריק' : 'File name cannot be empty');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        originalName: originalName.trim(),
        category,
        description,
        visibility: isAdmin ? visibility : file.visibility,
      };
      if (isAdmin && visibility === 'assigned') {
        payload.assignedUserIds = selectedUsers;
      }
      await onSave(payload);
    } catch (err) {
      setError(err.response?.data?.error || (isHe ? 'העדכון נכשל' : 'Update failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-start justify-center pt-8 md:pt-20 z-50 overflow-y-auto" onClick={onClose}>
      <div className="card w-full max-w-lg mx-4 mb-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b" style={{borderColor:'var(--border)'}}>
          <h2 className="text-lg font-semibold flex items-center gap-2" style={{color:'var(--text-primary)'}}>
            <Pencil className="w-5 h-5 text-blue-400" />
            {isHe ? 'עריכת קובץ' : 'Edit File'}
          </h2>
          <button onClick={onClose} className="btn-ghost"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
              <AlertCircle className="w-4 h-4" />{error}
            </div>
          )}

          {/* File Name */}
          <div>
            <label className="label">{isHe ? 'שם הקובץ' : 'File Name'}</label>
            <input
              type="text"
              className="input-field"
              value={originalName}
              onChange={e => setOriginalName(e.target.value)}
              required
            />
          </div>

          {/* Category */}
          <div>
            <label className="label">{isHe ? 'קטגוריה' : 'Category'}</label>
            <select className="select-field" value={category} onChange={e => setCategory(e.target.value)}>
              {Object.entries(CATEGORY_META).map(([key, meta]) => (
                <option key={key} value={key}>{isHe ? meta.labelHe : meta.label}</option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="label">{isHe ? 'תיאור' : 'Description'}</label>
            <input
              type="text"
              className="input-field"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={isHe ? 'למשל: גרסה 2.1' : 'e.g. Version 2.1'}
            />
          </div>

          {/* Visibility (admin only) */}
          {isAdmin && (
            <div>
              <label className="label">{isHe ? 'מי יראה?' : 'Visibility'}</label>
              <div className="space-y-2">
                {[
                  { val: 'private', label: isHe ? 'רק אני' : 'Only me', desc: isHe ? 'פרטי' : 'Private' },
                  { val: 'all', label: isHe ? 'כל המשתמשים' : 'All users', desc: isHe ? 'כולם יראו' : 'Visible to everyone' },
                  { val: 'assigned', label: isHe ? 'משתמשים ספציפיים' : 'Specific users', desc: isHe ? 'בחר למי' : 'Choose who' },
                ].map(opt => (
                  <label key={opt.val} className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer border transition-all"
                    style={{
                      borderColor: visibility === opt.val ? 'var(--brand)' : 'var(--border)',
                      background: visibility === opt.val ? 'var(--brand-light)' : 'transparent',
                    }}>
                    <input type="radio" name="editVis" value={opt.val} checked={visibility === opt.val}
                      onChange={() => setVisibility(opt.val)} className="accent-brand-500" />
                    <div>
                      <span className="text-sm font-medium" style={{color:'var(--text-primary)'}}>{opt.label}</span>
                      <span className="block text-xs" style={{color:'var(--text-muted)'}}>{opt.desc}</span>
                    </div>
                  </label>
                ))}
              </div>

              {visibility === 'assigned' && usersList.length > 0 && (
                <div className="mt-3 max-h-48 overflow-y-auto rounded-lg border p-2 space-y-1" style={{borderColor:'var(--border)', background:'var(--bg-card)'}}>
                  {usersList.filter(u => u.id !== currentUserId).map(u => (
                    <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-brand-500/5 cursor-pointer">
                      <input type="checkbox" className="accent-brand-500"
                        checked={selectedUsers.includes(u.id)}
                        onChange={e => {
                          if (e.target.checked) setSelectedUsers(prev => [...prev, u.id]);
                          else setSelectedUsers(prev => prev.filter(id => id !== u.id));
                        }}
                      />
                      <span className="text-sm" style={{color:'var(--text-primary)'}}>{u.displayName}</span>
                      <span className="text-xs" style={{color:'var(--text-muted)'}}>({u.email})</span>
                    </label>
                  ))}
                  {selectedUsers.length > 0 && (
                    <p className="text-xs px-2 pt-1" style={{color:'var(--brand)'}}>
                      {isHe ? `${selectedUsers.length} משתמשים נבחרו` : `${selectedUsers.length} users selected`}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* File info (read-only) */}
          <div className="flex items-center gap-4 px-3 py-2 rounded-lg text-xs" style={{background:'var(--bg-input)', color:'var(--text-muted)'}}>
            <span>{formatSize(file.fileSize)}</span>
            <span>•</span>
            <span>{file.mimeType || 'unknown'}</span>
            <span>•</span>
            <span>{new Date(file.createdAt).toLocaleDateString()}</span>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
              {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              {isHe ? 'שמור שינויים' : 'Save Changes'}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary">
              {isHe ? 'ביטול' : 'Cancel'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
